import { DashboardFiltersAPI } from '@/API/DashboardFilters'
import type { AppDispatch } from '@/redux'
import { dashboardCreationActions } from '@/redux/slice/dashboardCreationSlice'
import type { FilterValueDiscoveryContext } from '@/types/DashboardFilterDiscovery.types'

/**
 * DashboardFilterDiscoveryService — credential-driven filter value discovery
 * thunk (Stage 2B, TDD `plans/2026-04-28-dashboard-filter-discovery-tdd.md`
 * §Frontend Side §3).
 *
 * Companion mockups (per-state UI rendering lives in FiltersBlock):
 *   - happy path:  designs/dashboard-filter-discovery/2026-04-28-filter-{enrollment,financial,bunks}-v1.html
 *   - loading:     designs/dashboard-filter-discovery/2026-04-28-filter-enrollment-loading-v1.html
 *   - error:       designs/dashboard-filter-discovery/2026-04-28-filter-enrollment-error-v1.html
 *   - zero-seasons blocking (success + empty values for `season`):
 *       designs/dashboard-filter-discovery/2026-04-28-filter-enrollment-zero-seasons-v1.html
 *
 * Behaviour:
 *   1. dispatch setFilterStatus(loading)
 *   2. GET /v1/dashboard-domains/:domainId/filters/:filterCode/values
 *   3. on success → setFilterChoices(values) + setFilterStatus(ready)
 *   4. on 503 with credential_expired → setFilterStatus(credential_expired)
 *   5. on other failure → setFilterStatus(error)
 *
 * The thunk does NOT auto-apply default selections — that is the component
 * layer's responsibility (FiltersBlock applies `isCurrent` season pre-select
 * and other mockup-defined defaults). Centralising selection logic in the
 * component keeps the thunk side-effect-only and re-runnable on retry without
 * re-stomping user choices.
 */
export const discoverFilterValues = (
  token: string,
  domainId: string,
  filterCode: string,
  credentialId: string,
  parentContext: FilterValueDiscoveryContext = {}
) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(
      dashboardCreationActions.setFilterStatus({
        domainId,
        filterCode,
        status: 'loading',
      })
    )

    const result = await DashboardFiltersAPI.getFilterValues(token, {
      domainId,
      filterCode,
      credentialId,
      context: parentContext,
    })

    if (result.err !== undefined || !result.data) {
      // 503 with backend `error: 'CREDENTIAL_EXPIRED'` (or `reason:
      // 'credential_expired'`) signals that the SDK rejected the credential
      // mid-discovery. UI routes the user to the re-harvest CTA per mockup
      // 2026-04-28-filter-enrollment-error-v1.html.
      const isCredentialExpired =
        result.status === 503 &&
        (result.errorCode === 'CREDENTIAL_EXPIRED' ||
          result.errorCode === 'credential_expired')
      // 503 with backend `error: 'RATE_LIMITED'` carries `retryAfterSeconds`.
      // FE renders a countdown affordance and auto-retries. Bug #3 (2026-05-14).
      const isRateLimited =
        result.status === 503 && result.errorCode === 'RATE_LIMITED'

      let nextStatus: 'credential_expired' | 'rate_limited' | 'error'
      if (isCredentialExpired) {
        nextStatus = 'credential_expired'
      } else if (isRateLimited) {
        nextStatus = 'rate_limited'
      } else {
        nextStatus = 'error'
      }

      if (isRateLimited && result.retryAfterSeconds !== undefined) {
        dispatch(
          dashboardCreationActions.setFilterRateLimit({
            domainId,
            filterCode,
            retryAfterSeconds: result.retryAfterSeconds,
          })
        )
      } else {
        // Clear any prior rate-limit retry-after entry — we're not in that
        // state anymore (or we're in a different terminal failure).
        dispatch(
          dashboardCreationActions.setFilterRateLimit({
            domainId,
            filterCode,
            retryAfterSeconds: null,
          })
        )
      }

      dispatch(
        dashboardCreationActions.setFilterStatus({
          domainId,
          filterCode,
          status: nextStatus,
        })
      )
      return
    }

    // Success — clear any prior rate-limit retry-after entry.
    dispatch(
      dashboardCreationActions.setFilterRateLimit({
        domainId,
        filterCode,
        retryAfterSeconds: null,
      })
    )
    dispatch(
      dashboardCreationActions.setFilterChoices({
        domainId,
        filterCode,
        values: result.data.values,
      })
    )
    dispatch(
      dashboardCreationActions.setFilterStatus({
        domainId,
        filterCode,
        status: 'ready',
      })
    )
  }
